import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap-5';
import Button from 'shared/button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { deleteRequest, post, put } from 'helpers/api';
import { faReply, faTrash } from '@fortawesome/pro-solid-svg-icons';
import NestedBulkItemDropdown from './nested_bulk_item_dropdown';
import snakecaseKeys from 'snakecase-keys';
import confirm from 'components/confirm';

const DOC_ENTRY_TYPE = 'Docs::Document';
const FOLDER_ENTRY_TYPE = 'ProjectDocumentFolder';

// TODO: Consider to move all jquery code to its own custom hook to reduce the coupling with that technology
export default function BulkActionsButton({
  bulkDestroyDocsPath,
  bulkDestroyFoldersPath,
  bulkRestoreDocsPath,
  bulkRestoreFoldersPath,
  deleteAllPath,
  inRootFolder,
  saleAccessPath,
  leaseAccessPath
}) {
  const [showMain, setShowMain] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);

  const onOptionSelected = (access, saleOrLease) => {
    const body = {
      access: access,
      entries: selectedEntries
    };

    put(saleOrLease === 'sale' ? saleAccessPath : leaseAccessPath, body).then((response) => {
      response.forEach((responseItem) => {
        // eslint-disable-next-line max-len
        const dropdown = findCheckboxRow(responseItem.entryId, responseItem.entryType).querySelector(`.dropdown[data-vault-type="${saleOrLease}"]`);
        const updateDropdownEvent = new CustomEvent('updateDropdown', { detail: snakecaseKeys(responseItem) });
        dropdown.dispatchEvent(updateDropdownEvent);
      });
    });
  };

  const findCheckboxRow = (entryId, entryType) => {
    // eslint-disable-next-line max-len
    const checkbox = document.querySelector(`input.js-doc-center-row-checkbox[data-entry-id='${entryId}'][data-entry-type='${entryType}']`);
    return checkbox.closest('.js-doc-row');
  };

  const permanentlyDeleteRecycleBin = async () => {
    const confirmMessage = 'All documents in the Recycle Bin will be deleted. This action cannot be reversed.';

    try {
      await confirm(confirmMessage, { buttonLabel: 'Delete', buttonStyle: 'danger', title: 'Empty Recycle Bin?' });

      await deleteRequest(deleteAllPath);
      const emptyRecycleBinEvent = new CustomEvent('emptyRecycleBin');
      document.getElementById('doc-center').dispatchEvent(emptyRecycleBinEvent);
    } catch (e) {
      if (e) {
        // error that wasn't from dialog cancellation
        throw e;
      }
    }
  };

  const separateOptionsByType = () => {
    const docIds = [];
    const folderIds = [];
    const fadeRowEvent = new CustomEvent('fadeRow');

    selectedEntries.forEach((entry) => {
      findCheckboxRow(entry.entryId, entry.entryType).dispatchEvent(fadeRowEvent);

      if (entry.entryType === DOC_ENTRY_TYPE) {
        docIds.push(entry.entryId);
      } else if (entry.entryType === FOLDER_ENTRY_TYPE) {
        folderIds.push(entry.entryId);
      }
    });

    return { docIds, folderIds };
  };

  const restoreSelectedOptions = () => {
    const reloadTabEvent = new CustomEvent('reloadTab');
    const { docIds, folderIds } = separateOptionsByType();

    const promises = [];
    if (docIds.length > 0) promises.push(post(bulkRestoreDocsPath, { document_ids: docIds }));
    if (folderIds.length > 0) promises.push(post(bulkRestoreFoldersPath, { folder_ids: folderIds }));

    Promise.allSettled(promises).then(() => {
      document.getElementById('doc-center').dispatchEvent(reloadTabEvent);
    });
  };

  const deleteSelectedOptions = () => {
    const deleteRowEvent = new CustomEvent('deleteRow');
    const { docIds, folderIds } = separateOptionsByType();

    const promises = [];
    if (docIds.length > 0) promises.push(post(bulkDestroyDocsPath, { document_ids: docIds }));
    if (folderIds.length > 0) promises.push(post(bulkDestroyFoldersPath, { folder_ids: folderIds }));

    Promise.allSettled(promises).then((results) => {
      results.forEach((result) => {
        result.value.destroyedIds.forEach((id) => {
          findCheckboxRow(id, result.value.type).dispatchEvent(deleteRowEvent);
        });
      });

      setSelectedEntries([]);
    });

    setSelectedEntries([]);
  };

  useEffect(() => {
    const allDocsCheckbox = document.querySelector('.js-check-all-docs');

    if (!allDocsCheckbox) return;

    function allDocsChanged(event) {
      const nonDisabledCheckboxes = document.querySelectorAll('.js-doc-center-row-checkbox:not(:disabled)');
      nonDisabledCheckboxes.forEach(checkbox => checkbox.checked = allDocsCheckbox.checked);

      if ($(event.target).prop('checked')) {
        const allEntries = [...nonDisabledCheckboxes].map(checkbox => ({
          entryId: checkbox.dataset.entryId,
          entryType: checkbox.dataset.entryType
        }));

        setSelectedEntries(allEntries);
      } else {
        setSelectedEntries([]);
      }
    }

    allDocsCheckbox.addEventListener('change', allDocsChanged);

    return () => allDocsCheckbox.removeEventListener('change', allDocsChanged);
  }, []);

  useEffect(() => {
    const docCenterContainer = document.getElementById('doc-center');

    function checkboxChanged(event) {
      if (!event.target.classList.contains('js-doc-center-row-checkbox') || event.target.disabled) return;

      const { entryId, entryType } = event.target.dataset;
      if (event.target.checked) {
        setSelectedEntries(entries => [...entries, { entryId, entryType }]);
      } else {
        setSelectedEntries(
          entries => entries.filter(entry => (entry.entryId !== entryId || entry.entryType !== entryType))
        );
      }
    }

    docCenterContainer.addEventListener('change', checkboxChanged);

    return () => docCenterContainer.removeEventListener('change', checkboxChanged);
  }, []);

  const gatherDropdownOptions = () => {
    const inRecyclingBin = deleteAllPath !== '';
    const optionsSelected = selectedEntries.length > 0;
    const options = [];

    if (optionsSelected) {
      if (inRootFolder && !inRecyclingBin) {
        if (saleAccessPath) {
          const showPrefix = leaseAccessPath !== '';

          options.push({
            key: 'change-sale-access',
            Component: function Item() {
              return (
                <NestedBulkItemDropdown
                  elementId="ds-button--dropdown4"
                  nestedType="sale"
                  showPrefix={showPrefix}
                  onClick={onOptionSelected}
                />
              );
            }
          });
        }

        if (leaseAccessPath) {
          const showPrefix = saleAccessPath !== '';

          options.push({
            key: 'change-lease-access',
            Component: function Item() {
              return (
                <NestedBulkItemDropdown
                  elementId="ds-button--dropdown2"
                  nestedType="lease"
                  showPrefix={showPrefix}
                  onClick={onOptionSelected}
                />
              );
            }
          });
        }

        if (options.length > 0) {
          options.push({
            key: 'root-folder-divider',
            Component: function Item() {
              return <Dropdown.Divider className="my-2"/>;
            }
          });
        }
      }

      options.push({
        key: 'delete-option',
        Component: function Item() {
          return (
            <li>
              <Dropdown.Item className="px-4 py-2" onClick={deleteSelectedOptions}>
                <FontAwesomeIcon className="icon-container" icon={faTrash}/>
                {inRecyclingBin && 'Permanently '}Delete
              </Dropdown.Item>
            </li>
          );
        }
      });

      if (bulkRestoreDocsPath && bulkRestoreFoldersPath && inRecyclingBin) {
        options.push({
          key: 'restore-option',
          Component: function Item() {
            return (
              <li>
                <Dropdown.Item className="px-4 py-2" onClick={restoreSelectedOptions}>
                  <FontAwesomeIcon className="icon-container" icon={faReply}/>
                  Restore
                </Dropdown.Item>
              </li>
            );
          }
        });
      }
    }

    if (inRecyclingBin) {
      if (options.length > 0) {
        options.push({
          key: 'delete-all-divider',
          Component: function Item() {
            return <Dropdown.Divider className="my-2"/>;
          }
        });
      }

      options.push({
        key: 'delete-all-option',
        Component: function Item() {
          return (
            <li>
              <Dropdown.Item className="px-4 py-2" onClick={permanentlyDeleteRecycleBin}>
                <FontAwesomeIcon className="icon-container" icon={faTrash}/>
                Permanently Delete All
              </Dropdown.Item>
            </li>
          );
        }
      });
    }

    return options;
  };

  const dropdownOptions = gatherDropdownOptions();

  if (dropdownOptions.length === 0) return <></>;

  return (
    <Dropdown
      align="end"
      autoClose={true}
      id="ds-button--dropdown"
      show={showMain}
      onToggle={() => setShowMain(!showMain)}
    >
      <Button.Cancel iconRight={faChevronDown} outlined onClick={() => setShowMain(!showMain)}>
        Actions
      </Button.Cancel>

      <Dropdown.Menu as="ul" className="dropdown-menu-right">
        {dropdownOptions.map(DropdownOption => (
          <DropdownOption.Component key={DropdownOption.key}/>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

BulkActionsButton.propTypes = {
  bulkDestroyDocsPath: PropTypes.string.isRequired,
  bulkDestroyFoldersPath: PropTypes.string.isRequired,
  bulkRestoreDocsPath: PropTypes.string.isRequired,
  bulkRestoreFoldersPath: PropTypes.string.isRequired,
  deleteAllPath: PropTypes.string.isRequired,
  inRootFolder: PropTypes.bool.isRequired,
  leaseAccessPath: PropTypes.string,
  saleAccessPath: PropTypes.string
};
